import React, { useState, useRef } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import doc from "../../assets/doc.png"
import pdf from "../../assets/pdf.png"
import xls from "../../assets/xls.png"
import { CSVLink } from 'react-csv'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { Document, Packer, Paragraph, Table, TableRow, TableCell, WidthType, AlignmentType } from 'docx'
import { toast } from 'react-toastify'

const exportTypes = [
  { key: 'xls', icon: xls, alt: 'xls' },
  { key: 'pdf', icon: pdf, alt: 'pdf' },
  { key: 'doc', icon: doc, alt: 'doc' },
]

const ExportModal = ({
  onClose,
  csvLink,
  onExport,
  reportType = "report"
  // handleReject
}) => {
  const [selectedTypes, setSelectedTypes] = useState()
  const [exporting, setExporting] = useState(false)
  const csvRef = useRef()

  const toggleType = (type) => {
    // setSelectedTypes(prev =>
    //   prev.includes(type)
    //     ? prev.filter(t => t !== type)
    //     : [...prev, type]
    // )

    setSelectedTypes(type)
  }

  const generatePDF = () => {
    try {

      const doc = new jsPDF()

      // Add title
      const title = `${reportType.charAt(0).toUpperCase() + reportType.slice(1).replace('_', ' ')} Report`
      doc.setFontSize(16)
      doc.text(title, 14, 22)

      // Add date
      doc.setFontSize(10)
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 32)

      if (csvLink.data && csvLink.data.length > 0 && csvLink.headers && csvLink.headers.length > 0) {
        // Prepare table data
        const tableData = csvLink.data.map(row => {
          return csvLink.headers.map(header => {
            const value = row[header.key]
            return value !== undefined && value !== null ? String(value) : ''
          })
        })


        // Add table
        autoTable(doc, {
          head: [csvLink.headers.map(header => header.label)],
          body: tableData,
          startY: 40,
          styles: {
            fontSize: 8,
            cellPadding: 2,
          },
          headStyles: {
            fillColor: [255, 183, 51], // Yellow color matching theme
            textColor: [0, 0, 0],
            fontStyle: 'bold',
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
        })
      } else {
        doc.setFontSize(12)
        doc.text('No data available for export', 14, 50)
      }

      // Save the PDF
      doc.save(`${reportType}_report.pdf`)
    } catch (error) {
      throw error
    }
  }

  const generateDOC = async () => {
    try {
      if (!csvLink.data || csvLink.data.length === 0) {
        toast.error('No data available for export')
        return
      }

      if (!csvLink.headers || csvLink.headers.length === 0) {
        toast.error('No headers available for export')
        return
      }

      // Validate data structure
      if (!Array.isArray(csvLink.headers) || csvLink.headers.length === 0) {
        throw new Error('Invalid headers structure')
      }

      if (!Array.isArray(csvLink.data) || csvLink.data.length === 0) {
        throw new Error('Invalid data structure')
      }


      // Ensure all headers have required properties
      const validHeaders = csvLink.headers.filter(header =>
        header && typeof header === 'object' && header.label && header.key
      )

      if (validHeaders.length === 0) {
        throw new Error('No valid headers found')
      }

      // Helper function to format data for better display
      const formatCellValue = (value, headerKey) => {
        if (value === undefined || value === null || value === '') {
          return '-'
        }

        // Handle objects and arrays
        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '-'
          } else {
            // For objects, try to extract meaningful values
            const keys = Object.keys(value)
            if (keys.length === 0) return '-'

            // If it's a simple object with common fields, extract them
            if (value.name) return value.name
            if (value.title) return value.title
            if (value.label) return value.label
            if (value.id) return String(value.id)

            // Otherwise, show key-value pairs
            return keys.slice(0, 2).map(key => `${key}: ${value[key]}`).join(', ')
          }
        }

        const stringValue = String(value)

        // Format dates
        if (headerKey.includes('date') || headerKey.includes('created') || headerKey.includes('scheduled')) {
          try {
            const date = new Date(stringValue)
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            }
          } catch (e) {
            // If date parsing fails, return original value
          }
        }

        // Format currency
        if (headerKey.includes('amount') || headerKey.includes('price') || headerKey.includes('value')) {
          const num = parseFloat(stringValue)
          if (!isNaN(num)) {
            return `GHS ${num.toLocaleString()}`
          }
        }

        // Truncate very long text
        if (stringValue.length > 50) {
          return stringValue.substring(0, 47) + '...'
        }

        return stringValue
      }

      // Calculate column widths - use fixed pixel widths for better sizing
      const columnWidths = validHeaders.map(() => 4000) // Fixed 4000 twips per column (about 2.8 inches)

      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            new Paragraph({
              text: `${reportType.charAt(0).toUpperCase() + reportType.slice(1).replace('_', ' ')} Report`,
              heading: 'Heading1',
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 200,
              },
            }),
            new Paragraph({
              text: `Generated on: ${new Date().toLocaleDateString()}`,
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 400,
              },
            }),
            // Create table with better formatting
            new Table({
              width: {
                size: validHeaders.length * 2500, // Dynamic width based on number of columns
                type: WidthType.DXA,
              },
              margins: {
                top: 200,
                bottom: 200,
                left: 200,
                right: 200,
              },
              columnWidths: columnWidths,
              rows: [
                // Header row with better styling
                new TableRow({
                  children: validHeaders.map(header =>
                    new TableCell({
                      children: [new Paragraph({
                        text: header.label || 'Unknown',
                        bold: true,
                        alignment: AlignmentType.CENTER,
                      })],
                      shading: {
                        fill: "FFB733", // Yellow background
                        color: "FFB733",
                      },
                    })
                  ),
                }),
                // Data rows with better formatting
                ...csvLink.data.map((row, index) => {
                  return new TableRow({
                    children: validHeaders.map(header => {
                      const cellValue = formatCellValue(row[header.key], header.key)
                      return new TableCell({
                        children: [new Paragraph({
                          text: cellValue,
                          alignment: AlignmentType.LEFT,
                        })],
                        shading: {
                          fill: index % 2 === 0 ? "F8FAFC" : "FFFFFF", // Alternating row colors
                        },
                      })
                    }),
                  })
                }),
              ],
              borders: {
                top: { style: "single", size: 1, color: "000000" },
                bottom: { style: "single", size: 1, color: "000000" },
                left: { style: "single", size: 1, color: "000000" },
                right: { style: "single", size: 1, color: "000000" },
                insideHorizontal: { style: "single", size: 1, color: "CCCCCC" },
                insideVertical: { style: "single", size: 1, color: "CCCCCC" },
              },
            }),
          ],
        }],
      })

      // Generate and download the document
      const blob = await Packer.toBlob(doc)

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${reportType}_report.docx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)


    } catch (error) {
      throw error
    }
  }

  const handleExport = async () => {
    if (!selectedTypes) return

    setExporting(true)

    try {
      // Handle XLS (CSV) export
      if (selectedTypes === 'xls' && csvRef.current) {
        csvRef.current.link.click()
      }

      // Handle PDF export
      if (selectedTypes === 'pdf') {
        try {
          generatePDF()
        } catch (error) {
          alert('Error generating PDF. Please try again.')
        }
      }

      // Handle DOC export
      if (selectedTypes === 'doc') {
        try {
          await generateDOC()
        } catch (error) {
          alert(`Error generating DOC: ${error.message}. Please try again.`)
        }
      }

      // Call onExport for any additional formats
      // const otherTypes = selectedTypes.filter(t => !['xls', 'pdf', 'doc'].includes(t))
      if (onExport) {
        onExport(selectedTypes)
      }
    } catch (error) {
      alert('An unexpected error occurred during export. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full max-w-[400px] p-4 relative">
        <button
          onClick={() => onClose(false)}
          className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <IoIosCloseCircleOutline className="h-7 w-7 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-yellowcolor/10 rounded-full flex items-center justify-center mb-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 11.0008L21.2 2.80078" stroke="#8B6B0E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M21.9992 6.8V2H17.1992" stroke="#8B6B0E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13" stroke="#8B6B0E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <h2 className="text-xl font-bold mb-2 dark:text-white">
            Export {reportType.charAt(0).toUpperCase() + reportType.slice(1).replace('_', ' ')} Report
          </h2>
          <p className="text-gray-600 text-sm dark:text-gray-400">
            Kindly select the format to export data.
          </p>

          <div className='my-7 flex flex-row items-center justify-center'>
            {exportTypes.map(type => (
              <div key={type.key} className='w-full flex-col items-center justify-center spacy-y-2'>
                <button
                  type="button"
                  onClick={() => toggleType(type.key)}
                  disabled={exporting}
                  className={`mx-2 rounded-lg p-1 border transition-all duration-150 ${selectedTypes === type.key
                    ? 'border-yellowcolor bg-yellowcolor/10'
                    : 'border-black/20'
                    } ${exporting ? 'opacity-50 cursor-not-allowed' : 'hover:border-yellowcolor'} focus:outline-none`}
                  aria-pressed={selectedTypes === type.key}
                >
                  {type.key === 'xls' ? (
                    <>
                      <CSVLink {...csvLink} ref={csvRef} style={{ display: 'none' }} />
                      <img src={type.icon} alt={type.alt} className="w-14 h-14" />
                    </>
                  ) : (
                    <img src={type.icon} alt={type.alt} className="w-14 h-14" />
                  )}
                </button>
                <p className={`uppercase text-sm ${selectedTypes === type.key ? 'text-yellowcolor font-semibold' : 'dark:text-white'}`}>
                  {type.key}
                </p>
              </div>
            ))}
          </div>

          <div className="flex gap-4 w-full">
            <button
              className="flex-1 h-12 rounded-full border border-gray-400 dark:border-border-darkGray dark:text-white"
              onClick={() => onClose(false)}
              disabled={exporting}
            >
              Cancel
            </button>
            <button
              className={`flex-1 h-12 rounded-full bg-yellowcolor hover:bg-yellowcolor/80 text-black ${!selectedTypes || exporting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!selectedTypes || exporting}
              onClick={handleExport}
            >
              {exporting ? 'Exporting...' : 'Export Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExportModal